import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { RouteProp, useRoute } from '@react-navigation/native';

import PredictionsShareHeader from '@/assets/icons/predictionsShareHeader';
import SweepsShareHeader from '@/assets/icons/sweepsShareHeader';
import Winner from '@/assets/icons/winner';
import { InnerBorder } from '@/components/InnerBorder';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Odds } from '@/components/Odds';
import { Screen } from '@/components/ScreenComponent';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetSelectionIcon } from '@/feature/bets-sbk/components/bet-selections/BetSelectionIcon';
import { Bet, BetEvent, BetSelection } from '@/feature/bets-sbk/hooks/types';
import { useBetCost } from '@/feature/bets-sbk/hooks/use-bet-cost';
import { useBetDetailQuery } from '@/feature/bets-sbk/hooks/use-bet-detail';
import { usePotentialPayout } from '@/feature/bets-sbk/hooks/use-potential-payout';
import { getAmountWonWithBonus, getNoSweatRefund } from '@/feature/bets-sbk/utils/bet-promo-utils';
import { getMultiplier } from '@/feature/bets-sbk/utils/bet-summary-section-utils';
import { isSelectionInactive } from '@/feature/bets-sbk/utils/is-selection-inactive';
import { ShareEntryProvider } from '@/feature/entries-pickem/components/ShareEntryProvider';
import { SHARE_SCREEN_BG_COLOR } from '@/feature/entry-share/screens/EntryShare';
import { OptionName } from '@/feature/event-details-sbk/components/OptionName';
import { useMarketName } from '@/feature/event-details-sbk/hooks/use-market-name';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useSplitText } from '@/hooks/use-split-text';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { renderDate } from '@/utils/renderDate';
import { formatMatchupString } from '@/utils/sport-utils';
import { format } from 'date-fns';

import { useSbkBetShare } from '../hooks/use-sbk-bet-share';

type BetShareRouteProp = RouteProp<RootStackParamList, 'BetShareScreen'>;

export const BetShareScreen = () => (
    <ShareEntryProvider>
        <BetShareScreenLoader />
    </ShareEntryProvider>
);

const BetShareScreenLoader = () => {
    const route = useRoute<BetShareRouteProp>();
    const { bet, betId } = route.params;
    const { data, isLoading: isInitialLoading, refetch } = useBetDetailQuery(betId, bet);
    useFetchOnResume(refetch);
    const { forceRefreshing, onRefresh } = useForceRefresh(refetch);

    const isLoading = isInitialLoading || !data;
    if (isLoading) {
        return (
            <Screen>
                <Box flex={1} justifyContent="center" alignItems="center">
                    <LoadingSpinner size={24} displayLogo={false} />
                </Box>
            </Screen>
        );
    }

    return <BetShareScreenContent bet={data} forceRefreshing={forceRefreshing} onRefresh={onRefresh} />;
};

type BetShareScreenContentProps = {
    bet: Bet;
    forceRefreshing: boolean;
    onRefresh: () => void;
};

const BetShareScreenContent = ({ bet, forceRefreshing, onRefresh }: BetShareScreenContentProps) => {
    const viewRef = useRef<View>(null);
    useSbkBetShare(viewRef, bet);

    return (
        <SafeAreaView style={common.flex} edges={['top']}>
            <ScrollView
                style={styles.scroll}
                showsVerticalScrollIndicator={showScrollIndicator}
                refreshControl={
                    <RefreshControl
                        enabled
                        colors={[designSystem.colors.neutral700]}
                        tintColor={ds.colorValue.icon.primary}
                        refreshing={forceRefreshing}
                        onRefresh={onRefresh}
                    />
                }
            >
                <MaxWidthWrapper>
                    <BetShareContent bet={bet} viewRef={viewRef} />
                </MaxWidthWrapper>
            </ScrollView>
        </SafeAreaView>
    );
};

export const BetShareContent = ({ bet, viewRef }: { bet: Bet; viewRef?: React.Ref<View> }) => {
    const isWinner = bet.result === 'WON';

    return (
        <Box flex={1} backgroundColor={ds.colorToken.background.neutral.base} ref={viewRef}>
            {isWinner ? (
                <Box position="absolute" top={0} left={0} right={0} alignItems="center" pointerEvents="none">
                    <Winner />
                </Box>
            ) : null}
            <View collapsable={false} style={isWeb ? styles.contentContainer : null}>
                <ShareHeader />
                <BetSummaryHeader bet={bet} />
                <BetCardContainer bet={bet} />
                <PlacedFooter createdAt={bet.created_at} />
            </View>
        </Box>
    );
};

const ShareHeader = () => {
    const isPredictions = useIsPredictionsProduct();
    return (
        <Box alignItems="center" paddingTop="s20" paddingBottom="s16">
            {isPredictions ? <PredictionsShareHeader /> : <SweepsShareHeader />}
        </Box>
    );
};

const BetSummaryHeader = ({ bet }: { bet: Bet }) => {
    const { t } = useTranslation(['bets', 'betslip_sbk', 'common', 'bet_status']);
    const { formatCurrency } = useFormatCurrency();
    const isPredictions = useIsPredictionsProduct();
    const potentialPayout = usePotentialPayout(bet);
    const cost = useBetCost(bet);

    const { multiplier } = getMultiplier(bet);

    const getPayoutAndLabel = () => {
        switch (bet.result) {
            case 'CASHEDOUT':
                return {
                    payoutHeaderColor: 'neutral300' as const,
                    payoutColor: ds.colorToken.text.primary,
                    payoutLabel: isPredictions ? t('bet_status:SOLD') : t('bet_status:CASHEDOUT'),
                    payoutAmount: formatCurrency(getAmountWonWithBonus(bet)),
                };
            case 'CANCELLED':
            case 'PUSHED':
            case 'MANUALLY_CANCELLED':
                return {
                    payoutHeaderColor: 'neutral300' as const,
                    payoutColor: ds.colorToken.text.primary,
                    payoutLabel: t('common:refunded'),
                    payoutAmount: formatCurrency(cost),
                };
            case 'WON':
                return {
                    payoutHeaderColor: ds.colorToken.text.success,
                    payoutColor: ds.colorToken.text.success,
                    payoutLabel: t('bets:paid'),
                    payoutAmount: formatCurrency(getAmountWonWithBonus(bet)),
                };
            case 'LOST': {
                // A rewarded No Sweat loss pays a Betr Bucks refund; mirror the card/details "Bonus payout".
                const refund = getNoSweatRefund(bet);
                return {
                    payoutHeaderColor: 'neutral300' as const,
                    payoutColor: ds.colorToken.text.primary,
                    payoutLabel: refund != null ? t('bets:bonus_payout') : t('bets:paid'),
                    payoutAmount: refund != null ? formatCurrency(refund) : '--',
                };
            }
            default:
                return {
                    payoutHeaderColor: 'neutral300' as const,
                    payoutColor: ds.colorToken.text.primary,
                    payoutLabel: t('betslip_sbk:payout'),
                    payoutAmount: formatCurrency(potentialPayout),
                };
        }
    };

    const { payoutLabel, payoutAmount, payoutHeaderColor, payoutColor } = getPayoutAndLabel();

    return (
        <Row paddingHorizontal="s16" alignItems="center" gap="s12">
            <Column flex={1} alignItems="center" paddingHorizontal="s8" gap="s2">
                <Text variant="bodySmaller" lineHeight={16} color="neutral300">
                    {isPredictions ? t('bets:cost') : t('common:entry')}
                </Text>
                <Text
                    variant="titleLarge"
                    fontSize={18}
                    fontWeight={700}
                    letterSpacing={-0.45}
                    color={ds.colorToken.text.primary}
                >
                    {formatCurrency(cost)}
                </Text>
            </Column>
            <Column alignItems="center" justifyContent="center" gap="s4">
                <Box style={styles.pillDivider} />
                <Box
                    paddingHorizontal="s8"
                    paddingVertical="s4"
                    borderRadius="r8"
                    style={{ backgroundColor: withOpacity(ds.colorValue.background.brand.strong, 0.1) }}
                >
                    <Odds odds={multiplier} variant="labelMedium" color={ds.colorToken.text.brand} />
                </Box>
                <Box style={styles.pillDivider} />
            </Column>
            <Column flex={1} alignItems="center" paddingHorizontal="s8" gap="s2">
                <Text variant="bodySmaller" lineHeight={16} color={payoutHeaderColor}>
                    {payoutLabel}
                </Text>
                <Text variant="titleLarge" fontSize={18} fontWeight={700} letterSpacing={-0.45} color={payoutColor}>
                    {payoutAmount}
                </Text>
            </Column>
        </Row>
    );
};

const BetCardContainer = ({ bet }: { bet: Bet }) => (
    <Box
        marginHorizontal="s16"
        marginTop="s16"
        padding="s16"
        borderRadius="r20"
        backgroundColor={ds.colorToken.background.neutral.subtler}
    >
        <InnerBorder borderRadius="r20" borderColor={ds.colorValue.border.subtle} />
        {bet.events.map((event, index) => (
            <EventGroup key={event.id} event={event} isLast={index === bet.events.length - 1} />
        ))}
    </Box>
);

const EventGroup = ({ event, isLast }: { event: BetEvent; isLast: boolean }) => {
    const awayCode = event.away_team?.short_code ?? '';
    const homeCode = event.home_team?.short_code ?? '';
    const matchup = formatMatchupString(homeCode, awayCode, event.sport.name);
    const dateText = renderDate(new Date(event.start_time));
    const header = [matchup, dateText].filter(Boolean).join(' • ');

    return (
        <>
            <Box marginBottom="s8">
                <Text variant="bodySmall" color="neutral300">
                    {header}
                </Text>
            </Box>
            <Box gap="s8">
                {event.selections.map(selection => (
                    <SelectionRow key={selection.id} selection={selection} event={event} />
                ))}
            </Box>
            {!isLast ? (
                <Box paddingVertical="s12">
                    <Box height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
                </Box>
            ) : null}
        </>
    );
};

const SelectionRow = ({ selection, event }: { selection: BetSelection; event: BetEvent }) => {
    const isRevived = selection.feature_flags?.includes('REVIVE');
    const isInactive = isSelectionInactive(selection);
    const textDecorationLine = isInactive ? 'line-through' : 'none';

    const marketName = useMarketName({
        event,
        sportName: event.sport.name,
        marketType: selection.market.market_type,
        fallback: selection.market.description,
        player: selection.market.player,
        appendPlayerName: true,
        appendMicroMarketDescription: selection.market.is_micro_market,
        appendTeamName: !selection.market.is_micro_market,
        isExtendedName: true,
    });

    const { Line1, Line2 } = useSplitText({
        text: marketName,
        color: isInactive ? 'neutral400' : 'neutral300',
        textDecorationLine,
    });

    return (
        <Row alignItems="flex-start" gap="s12">
            <Box height={24} justifyContent="center">
                <BetSelectionIcon variant="s" result={selection.result} isRevived={isRevived} />
            </Box>
            <Box flex={1}>
                <Row alignItems="center" gap="s12">
                    <OptionName
                        optionType={selection.option.option_type}
                        marketType={selection.market.market_type}
                        homeTeam={event.home_team}
                        awayTeam={event.away_team}
                        fallback={selection.option.description}
                        sportName={event.sport.name}
                        variant="titleMedium"
                        color={isInactive ? 'neutral400' : ds.colorToken.text.primary}
                        textDecorationLine={textDecorationLine}
                    />
                    <Box width={1} height={16} backgroundColor={ds.colorToken.background.neutral.subtler} />
                    {Line1}
                </Row>
                {Line2}
            </Box>
        </Row>
    );
};

const PlacedFooter = ({ createdAt }: { createdAt: number }) => {
    const { t } = useTranslation('bets');
    const formatted = format(new Date(createdAt), "MMMM d, yyyy '@' h:mm a");
    return (
        <Box alignItems="center" paddingTop="s32" paddingBottom="s24" paddingHorizontal="s16">
            <Text variant="bodySmaller" lineHeight={16} color="neutral300">
                {`${t('placed')}: ${formatted}`}
            </Text>
        </Box>
    );
};

const styles = StyleSheet.create({
    scroll: {
        backgroundColor: SHARE_SCREEN_BG_COLOR,
    },
    contentContainer: {
        position: 'absolute',
        width: '100%',
    },
    pillDivider: {
        width: 1,
        height: 8,
        backgroundColor: ds.colorValue.border.strong,
    },
});
